// Locale-aware money/number formatting. All functions read the current UI locale.
import { intlTag } from '@/i18n';

const ZERO_DECIMAL = new Set(['JPY']);

function digitsFor(currency: string): number {
  return ZERO_DECIMAL.has(currency.toUpperCase()) ? 0 : 2;
}

export function formatMoney(amount: number | null | undefined, currency: string, opts: { sign?: boolean } = {}): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const c = currency.toUpperCase();
  const digits = digitsFor(c);
  return new Intl.NumberFormat(intlTag(), {
    style: 'currency', currency: c, currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits, maximumFractionDigits: digits,
    signDisplay: opts.sign ? 'exceptZero' : 'auto',
  }).format(amount);
}

/** Splits a formatted amount into the integer part and the rest (decimal, fraction, trailing symbol) for large-number typography. */
export function formatMoneyParts(amount: number | null | undefined, currency: string): { main: string; tail: string } {
  if (amount == null || !Number.isFinite(amount)) return { main: '—', tail: '' };
  const c = currency.toUpperCase();
  const digits = digitsFor(c);
  const parts = new Intl.NumberFormat(intlTag(), { style: 'currency', currency: c, currencyDisplay: 'narrowSymbol', minimumFractionDigits: digits, maximumFractionDigits: digits }).formatToParts(amount);
  const idx = parts.findIndex((p) => p.type === 'decimal');
  if (idx < 0) return { main: parts.map((p) => p.value).join(''), tail: '' };
  return { main: parts.slice(0, idx).map((p) => p.value).join(''), tail: parts.slice(idx).map((p) => p.value).join('') };
}

export function formatNumber(n: number | null | undefined, maxDigits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(intlTag(), { maximumFractionDigits: maxDigits, minimumFractionDigits: Math.min(2, maxDigits) }).format(n);
}

export function formatQty(n: number): string {
  return new Intl.NumberFormat(intlTag(), { maximumFractionDigits: Number.isInteger(n) ? 0 : 8 }).format(n);
}

/** `n` is already in percent (0.54 → "+0.54%"). */
export function formatPct(n: number | null | undefined, sign = true): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(intlTag(), { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: sign ? 'exceptZero' : 'auto' }).format(n / 100);
}

export function formatPrice(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const digits = n >= 1 ? 2 : 4;
  return new Intl.NumberFormat(intlTag(), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

export function formatRate(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(intlTag(), { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n);
}

/** Plain number with an explicit sign, e.g. "+1.24" / "−0.80". */
export function formatSigned(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(intlTag(), { minimumFractionDigits: digits, maximumFractionDigits: digits, signDisplay: 'exceptZero' }).format(n);
}

/** Compact currency, e.g. "$3.0T" / "3.0万亿美元". */
export function formatCompactMoney(amount: number | null | undefined, currency: string): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat(intlTag(), { style: 'currency', currency: currency.toUpperCase(), notation: 'compact', maximumFractionDigits: 1 }).format(amount);
}
